import type { WritableComputedRef } from 'vue';
import type { AnyItem, CompositePrimaryKey, ConnectToMany, CreateToMany, RelationOperationToMany, ResolvedMonospaceSingleRelationField } from '#layers/data-model/public';
import type { CreateItem } from '#layers/forms/composables/relation-field-state-primitives';
import { toValue } from 'vue';
import { isNullishOrEmpty, randomUuid } from '#layers/base/public';
import { getItemPrimaryKey, getM2MStructureSafe, isConnectToMany, isConnectToOne, isCreateToMany, isDeleteToMany, isDisconnectToMany, isSameKey, isUpdateToMany } from '#layers/data-model/public';
import { createdItemKey, originalItemKey, verifyItemKeys } from '#layers/forms/composables/relation-field-state-primitives';

function getCreatedItemKey(item: AnyItem) {
	return item[createdItemKey];
}

export function createRelationFieldStateToManyHandler(field: ResolvedMonospaceSingleRelationField, fieldValue: WritableComputedRef<RelationOperationToMany[], RelationOperationToMany[]>) {
	const m2mStructure = getM2MStructureSafe(toValue(field));

	function operations() {
		return fieldValue.value ?? [];
	}

	function appendOperation(operation: RelationOperationToMany) {
		fieldValue.value = [...operations(), operation];
	}

	function replaceOperation(index: number, operation: RelationOperationToMany) {
		fieldValue.value = operations().map((existing, existingIndex) => existingIndex === index ? operation : existing);
	}

	function handleCreate(payload: AnyItem) {
		const createOperationIndex = operations().findIndex((op) => isCreateToMany(op));
		const createdItem = { ...payload, [createdItemKey]: randomUuid() };

		if (createOperationIndex === -1) {
			appendOperation({ _create: { data: [createdItem] } });
		}
		else {
			const createOperation = operations()[createOperationIndex] as CreateToMany;
			const itemIndex = createOperation._create.data.findIndex((item) => getCreatedItemKey(item) === getCreatedItemKey(payload));
			const data = itemIndex !== -1
				? createOperation._create.data.map((item, index) => index === itemIndex ? createdItem : item)
				: [...createOperation._create.data, createdItem];

			replaceOperation(createOperationIndex, { _create: { data } });
		}
	}

	function handleUndoCreate(payload: CreateItem) {
		fieldValue.value = fieldValue.value?.map((outerOp) => {
			if (isCreateToMany(outerOp)) {
				const data = outerOp._create?.data ?? [];
				return { _create: { data: data.filter((item) => getCreatedItemKey(item) !== getCreatedItemKey(payload)) } };
			}
			else {
				return outerOp;
			}
		});

		cleanManyOperations();
	}

	function handleConnect(key: CompositePrimaryKey) {
		handleUndoDisconnect(key);

		const connectOperationIndex = operations().findIndex((op) => isConnectToMany(op));

		if (connectOperationIndex === -1) {
			appendOperation({ _connect: { keys: [key] } });
		}
		else {
			const connectOperation = operations()[connectOperationIndex] as ConnectToMany;
			replaceOperation(connectOperationIndex, { _connect: { keys: [...connectOperation._connect.keys, key] } });
		}
	}

	function handleDisconnect(key: CompositePrimaryKey) {
		const foundConnect = handleUndoConnect(key);

		if (!foundConnect) {
			appendOperation({ _disconnect: { filter: key } });
		}
	}

	/**
	 * Reverts a _connect, by removing both _connect and _update operations from the state
	 *
	 * @return boolean Indicates if some operations have been removed
	 */
	function handleUndoConnect(key: CompositePrimaryKey) {
		if (isNullishOrEmpty(fieldValue.value)) {
			return false;
		}

		let foundConnect = false;

		fieldValue.value = fieldValue.value.filter((op) => {
			return !(isUpdateToMany(op) && isSameKey(op._update.filter as CompositePrimaryKey, key));
		}).map((outerOp) => {
			if (isConnectToMany(outerOp)) {
				const keys: CompositePrimaryKey[] = outerOp._connect?.keys ?? [];
				return { _connect: { keys: keys.filter((k) => {
					if (isSameKey(k, key)) {
						foundConnect = true;
					}

					return !isSameKey(k, key);
				}) } };
			}
			else {
				return outerOp;
			}
		});

		cleanManyOperations();
		return foundConnect;
	}

	function handleUndoDisconnect(key: CompositePrimaryKey) {
		if (isNullishOrEmpty(fieldValue.value)) {
			return;
		}

		fieldValue.value = fieldValue.value.filter((op) =>
			!isDisconnectToMany(op) || !isSameKey(op._disconnect.filter as CompositePrimaryKey, key),
		);
	}

	function handleUpdate(originalKey: CompositePrimaryKey | null, payload: AnyItem) {
		originalKey ??= getItemPrimaryKey(payload, toValue(field).oppositeCollection!);
		verifyItemKeys(originalKey, payload, toValue(field).oppositeCollection!);

		const updatedIndex = operations().findIndex((op) => originalKey && isUpdateToMany(op) && isSameKey(op._update.filter as CompositePrimaryKey, originalKey));
		const updateOperation = { _update: { filter: originalKey, data: { ...payload, [originalItemKey]: originalKey } } };

		if (updatedIndex !== -1) {
			replaceOperation(updatedIndex, updateOperation);
			return;
		}

		const createOperationIndex = operations().findIndex((op) => isCreateToMany(op));

		if (createOperationIndex !== -1) {
			// check if the item is already in the create operation
			const createOperation = operations()[createOperationIndex] as CreateToMany;
			const itemKey = getCreatedItemKey(payload);
			const createdIndex = createOperation._create.data.findIndex((item) => getCreatedItemKey(item) === itemKey);

			if (createdIndex !== -1) {
				const innerCreateOperation: AnyItem = { ...payload, [createdItemKey]: itemKey };
				const data = createOperation._create.data.map((item, index) => index === createdIndex ? innerCreateOperation : item);
				replaceOperation(createOperationIndex, { _create: { data } });
				return;
			}
		}

		appendOperation(updateOperation);
	}

	function handleDelete(key: CompositePrimaryKey) {
		// First undo any pending create for this junction
		const foundCreate = handleUndoCreateByKey(key);

		// Only add delete if it was an existing item (not a pending create)
		if (!foundCreate) {
			appendOperation({ _delete: { filter: key } });
		}
	}

	function handleUndoDelete(key: CompositePrimaryKey) {
		if (isNullishOrEmpty(fieldValue.value)) {
			return;
		}

		fieldValue.value = fieldValue.value.filter((op) =>
			!isDeleteToMany(op) || !isSameKey(op._delete.filter as CompositePrimaryKey, key),
		);
	}

	/**
	 * Match a junction item against a composite key, handling nested _connect structures.
	 * For M2M creates, the target FK is nested: { targetRelation: { _connect: { key: {...} } } }
	 * The source FK is implicit (not stored in the item) since creates happen via the source O2M field.
	 */
	function matchJunctionItemByKey(item: AnyItem, key: CompositePrimaryKey): boolean {
		if (!m2mStructure) {
			// Not M2M, use direct matching only
			return Object.keys(key).every((k) => item[k] === key[k]);
		}

		const toTargetField = m2mStructure.junction.toTarget;
		const toTargetFieldName = toTargetField.apiName;
		const toTargetConstraint = toTargetField.constraint;
		const toSourceConstraint = m2mStructure.junction.toSource?.constraint;

		// Check each key field
		return Object.entries(key).every(([keyField, keyValue]) => {
			// First, check direct value
			if (item[keyField] === keyValue) {
				return true;
			}

			// Check if this key field is the FK to source (implicit in creates via O2M)
			if (toSourceConstraint) {
				const sourceMapping = toSourceConstraint.fieldMappings.find(
					(m: AnyItem) => m.constrainedField?.apiName === keyField,
				);

				if (sourceMapping && !(keyField in item)) {
					// Source FK is implicit - all creates in this context belong to the current source
					return true;
				}
			}

			// Check if this key field is a FK to target (via constraint)
			if (toTargetConstraint) {
				const mapping = toTargetConstraint.fieldMappings.find(
					(m: AnyItem) => m.constrainedField?.apiName === keyField,
				);

				if (mapping) {
					// This key field is the FK to target, check nested _connect
					const targetRelation = item[toTargetFieldName];

					if (isConnectToOne(targetRelation)) {
						const referencedFieldName = mapping.referencedField?.apiName;

						if (referencedFieldName) {
							return targetRelation._connect.key[referencedFieldName] === keyValue;
						}
					}
				}
			}

			return false;
		});
	}

	/**
	 * Undo a create by matching junction key (for M2M).
	 * Returns true if a create was found and removed.
	 */
	function handleUndoCreateByKey(key: CompositePrimaryKey): boolean {
		if (isNullishOrEmpty(fieldValue.value)) {
			return false;
		}

		let found = false;

		fieldValue.value = fieldValue.value.map((outerOp) => {
			if (isCreateToMany(outerOp)) {
				const data = outerOp._create?.data ?? [];
				const filtered = data.filter((item) => {
					// Match by junction key fields (handles both direct FK and nested _connect)
					const matches = matchJunctionItemByKey(item, key);
					if (matches) found = true;
					return !matches;
				});
				return { _create: { data: filtered } };
			}

			return outerOp;
		});

		cleanManyOperations();
		return found;
	}

	function cleanManyOperations() {
		fieldValue.value = fieldValue.value?.filter((op) => {
			const isEmptyCreate = isCreateToMany(op) && op._create.data.length === 0;
			const isEmptyConnect = isConnectToMany(op) && op._connect.keys.length === 0;
			return !isEmptyCreate && !isEmptyConnect;
		});
	}

	return {
		handleConnect,
		handleUndoConnect,
		handleDisconnect,
		handleUndoDisconnect,
		handleCreate,
		handleUndoCreate,
		handleDelete,
		handleUndoDelete,
		handleUpdate,
	};
}
